/**
 * Tipos para IAPHR2Service — Patrimonio Cultural de Andalucía
 *
 * Datos: IAPH (Instituto Andaluz del Patrimonio Histórico)
 * Incluye: Inmuebles protegidos y catalogados
 * CRS: EPSG:25830 (UTM30 ETRS89)
 * Ruta R2: /iaph/{ine5}.json.gz
 *
 * @module types/iaph
 * @version 1.0.0
 */

// =============================================================================
// FEATURE — Esquema generado por iaph-download.ts
// =============================================================================

/** Feature IAPH almacenada en R2 */
export interface IAPHFeature {
  /** ID IAPH interno */
  id: number;
  /** Código IAPH (ej: "01180230001") */
  codigo: string;
  /** Nombre del bien patrimonial */
  nombre: string;
  /** Tokens pre-normalizados para búsqueda (lowercase, sin tildes) */
  tokens: string;
  /** Coordenada X UTM30 EPSG:25830 */
  x: number;
  /** Coordenada Y UTM30 EPSG:25830 */
  y: number;
  /** Código INE municipio (5 dígitos) */
  cod_ine: string;
  /** Nombre municipio */
  municipio: string;
  /** Provincia */
  provincia: string;
  /** Tipo de bien patrimonial */
  tipo: string;
  /** Bien protegido (BIC, catalogado, etc.) */
  protegido: boolean;
}

// =============================================================================
// OPCIONES DE BÚSQUEDA
// =============================================================================

/** Opciones para buscar en IAPH R2 */
export interface IAPHSearchOptions {
  /** Código INE del municipio (5 dígitos) */
  codINE: string;
  /** Nombre del bien a buscar */
  name: string;
  /** Confianza mínima para aceptar resultado (0-100) */
  minConfidence?: number;
  /** Máximo de resultados */
  maxResults?: number;
  /** Filtrar solo protegidos */
  onlyProtected?: boolean;
}

// =============================================================================
// RESULTADO
// =============================================================================

/** Resultado de búsqueda IAPH */
export interface IAPHResult {
  /** Coordenada X UTM30 EPSG:25830 */
  x: number;
  /** Coordenada Y UTM30 EPSG:25830 */
  y: number;
  /** Confianza del match (0-100) */
  confidence: number;
  /** Precisión en metros */
  precision: number;
  /** Fuente */
  source: 'iaph_r2';
  /** Feature original */
  feature: IAPHFeature;
  /** Score fuzzy (0-1) si aplica */
  fuzzyScore?: number;
}

// =============================================================================
// CONSTANTES DE VALIDACIÓN
// =============================================================================

/** Rango X válido para coordenadas IAPH Andalucía (EPSG:25830) */
export const IAPH_X_RANGE = { min: 100_000, max: 600_000 } as const;

/** Rango Y válido para coordenadas IAPH Andalucía (EPSG:25830) */
export const IAPH_Y_RANGE = { min: 3_980_000, max: 4_290_000 } as const;

/** Precisión estimada en metros (coordenadas IAPH son aproximadas) */
export const IAPH_PRECISION_M = 25;
